// libs
import React, { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message, Tooltip } from 'antd';

// graphql
import { useBatchAddTermsMutation, useBatchSetDomainMutation } from '../../../../graphql/mutations.generated';
import { useBatchSetDataProductMutation } from '../../../../graphql/dataProduct.generated';

// components
import { SearchSelectModal } from '../components/styled/search/SearchSelectModal';
import CreateDomainModal from '../../../domain/CreateDomainModal';

// services
import { useDomainsContext } from '../../../domain/DomainsContext';
import { useTranslation } from '../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { updateListDomainsCache } from '../../../domain/utils';
import { ENTITY_ACTIONS } from '../rbacConstantsService';
import { EntityType } from '../../../../types.generated';
import { EntityCapabilityType } from '../../Entity';
import { useEntityContext } from '../EntityContext';
import { handleBatchError } from '../utils';
import { useRbac } from '../useRbac';

export enum EntityActionItem {
    /**
     * Batch add a Glossary Term to a set of assets
     */
    BATCH_ADD_GLOSSARY_TERM,
    /**
     * Batch add a Domain to a set of assets
     */
    BATCH_ADD_DOMAIN,
    /**
     * Batch add a Data Product to a set of assets
     */
    BATCH_ADD_DATA_PRODUCT,
}

interface Props {
    urn: string;
    actionItems: Set<EntityActionItem>;
    refetchForEntity?: () => void;
}

function EntityActions(props: Props) {
    const { t } = useTranslation();
    // eslint ignore react/no-unused-prop-types
    const entityRegistry = useEntityRegistry();
    const { urn, actionItems, refetchForEntity } = props;
    const { setShouldRefetchEmbeddedListSearch } = useEntityContext();

    const { canPerformEntityMenuAction } = useRbac();
    const canCreateDomain = canPerformEntityMenuAction(EntityType.Domain, ENTITY_ACTIONS.CREATE);
    const tooltipCreateDomain = !canCreateDomain ? t('UNAUTHORISED_TOOLTIP') : undefined;

    const [isBatchAddGlossaryTermModalVisible, setIsBatchAddGlossaryTermModalVisible] = useState(false);
    const [isBatchSetDataProductModalVisible, setIsBatchSetDataProductModalVisible] = useState(false);
    const [isBatchSetDomainModalVisible, setIsBatchSetDomainModalVisible] = useState(false);
    const [isNewDomainModalVisible, setIsNewDomainModalVisible] = useState(false);

    const [batchSetDataProductMutation] = useBatchSetDataProductMutation();
    const [batchSetDomainMutation] = useBatchSetDomainMutation();
    const [batchAddTermsMutation] = useBatchAddTermsMutation();
    const { setParentDomainsToUpdate } = useDomainsContext();
    const client = useApolloClient();

    // eslint-disable-next-line
    const batchAddGlossaryTerms = (entityUrns: Array<string>) => {
        batchAddTermsMutation({
            variables: {
                input: {
                    termUrns: [urn],
                    resources: entityUrns.map((entityUrn) => ({
                        resourceUrn: entityUrn,
                    })),
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    setIsBatchAddGlossaryTermModalVisible(false);
                    message.loading({ content: t('Updating'), duration: 3 });
                    setTimeout(() => {
                        message.success({
                            content: t('Added Glossary Term to entities!'),
                            duration: 2,
                        });
                        refetchForEntity?.();
                        setShouldRefetchEmbeddedListSearch?.(true);
                    }, 3000);
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(entityUrns, e, {
                        content: `${t('Failed to add glossary term:')} \n ${e.message || ''}`,
                        duration: 3,
                    }),
                );
            });
    };

    // eslint-disable-next-line
    const batchSetDomain = (entityUrns: Array<string>) => {
        batchSetDomainMutation({
            variables: {
                input: {
                    domainUrn: urn,
                    resources: entityUrns.map((entityUrn) => ({
                        resourceUrn: entityUrn,
                    })),
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    setIsBatchSetDomainModalVisible(false);
                    message.loading({ content: t('Updating'), duration: 3 });
                    setTimeout(() => {
                        message.success({
                            content: t('Added assets to Domain!'),
                            duration: 3,
                        });
                        refetchForEntity?.();
                        setShouldRefetchEmbeddedListSearch?.(true);
                    }, 3000);
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(entityUrns, e, {
                        content: `${t('Failed to add assets to Domain:')} \n ${e.message || ''}`,
                        duration: 3,
                    }),
                );
            });
    };

    // eslint-disable-next-line
    const batchSetDataProduct = (entityUrns: Array<string>) => {
        batchSetDataProductMutation({
            variables: {
                input: {
                    dataProductUrn: urn,
                    resourceUrns: entityUrns,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    setIsBatchSetDataProductModalVisible(false);
                    message.loading({ content: t('Updating...'), duration: 3 });
                    setTimeout(() => {
                        message.success({
                            content: t('Added assets to Data Product!'),
                            duration: 3,
                        });
                        refetchForEntity?.();
                        setShouldRefetchEmbeddedListSearch?.(true);
                    }, 3000);
                }
            })
            .catch((e) => {
                message.destroy();
                message.error(
                    handleBatchError(entityUrns, e, {
                        content: t('Failed to add assets to Data Product. An unknown error occurred.'),
                        duration: 3,
                    }),
                );
            });
    };

    return (
        <>
            {actionItems.has(EntityActionItem.BATCH_ADD_GLOSSARY_TERM) && (
                <Button onClick={() => setIsBatchAddGlossaryTermModalVisible(true)}>
                    <LinkOutlined /> <span> {t('Add to assets')}</span>
                </Button>
            )}
            {actionItems.has(EntityActionItem.BATCH_ADD_DOMAIN) && (
                <>
                    <Button type="text" className="f-btn-flat" onClick={() => setIsBatchSetDomainModalVisible(true)}>
                        <LinkOutlined /> <span>{t('Add to assets')}</span>
                    </Button>

                    <Tooltip title={tooltipCreateDomain} placement="left" overlayClassName="f-tooltip-white">
                        <Button disabled={!canCreateDomain} onClick={() => setIsNewDomainModalVisible(true)}>
                            <PlusOutlined /> {t('New Domain')}
                        </Button>
                    </Tooltip>
                </>
            )}
            {actionItems.has(EntityActionItem.BATCH_ADD_DATA_PRODUCT) && (
                <Button onClick={() => setIsBatchSetDataProductModalVisible(true)}>
                    <LinkOutlined /> <span>{t('Add assets')}</span>
                </Button>
            )}
            {isBatchAddGlossaryTermModalVisible && (
                <SearchSelectModal
                    titleText={t('Add Glossary Term to assets')}
                    continueText={t('Add')}
                    showLessPaginationItems
                    onContinue={batchAddGlossaryTerms}
                    onCancel={() => setIsBatchAddGlossaryTermModalVisible(false)}
                    fixedEntityTypes={Array.from(
                        entityRegistry.getTypesWithSupportedCapabilities(EntityCapabilityType.GLOSSARY_TERMS),
                    )}
                />
            )}
            {isBatchSetDomainModalVisible && (
                <SearchSelectModal
                    showLessPaginationItems
                    titleText={t('Add assets to Domain')}
                    continueText={t('Add')}
                    onContinue={batchSetDomain}
                    onCancel={() => setIsBatchSetDomainModalVisible(false)}
                    fixedEntityTypes={Array.from(
                        entityRegistry.getTypesWithSupportedCapabilities(EntityCapabilityType.DOMAINS),
                    )}
                />
            )}
            {isBatchSetDataProductModalVisible && (
                <SearchSelectModal
                    showLessPaginationItems
                    titleText={t('Add assets to Data Product')}
                    continueText={t('Add')}
                    onContinue={batchSetDataProduct}
                    onCancel={() => setIsBatchSetDataProductModalVisible(false)}
                    fixedEntityTypes={Array.from(
                        entityRegistry.getTypesWithSupportedCapabilities(EntityCapabilityType.DATA_PRODUCTS),
                    )}
                />
            )}
            {isNewDomainModalVisible && (
                <CreateDomainModal
                    onClose={() => setIsNewDomainModalVisible(false)}
                    onCreate={(domainUrn, id, name, description, parentDomain) => {
                        updateListDomainsCache(client, domainUrn, id, name, description, parentDomain);
                        if (parentDomain) setParentDomainsToUpdate([parentDomain]);
                    }}
                />
            )}
        </>
    );
}

export default EntityActions;
